/** @jsx jsx */
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { navigate } from 'gatsby'
import { identify } from '@chordcommerce/chord-js-autonomy/dist/analytics/utils'
import { useCart, useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import { Box, Button, jsx, Spinner, Text } from 'theme-ui'
import AgeVerification from './AgeVerification'
import AttentiveOptIn from '~/components/Attentive/OptIn'
import { useBulkMeter } from '~/hooks/components/use-bulk-meter'
import { useOrderTags } from '~/hooks/components/use-order-tags'
import useUiTextQuery from '~/hooks/graphql/queries/use-ui-text'
import { storeTransactionalNumber } from '~/services/attentive/client'

const CheckoutButton = ({ includeSmsOptIn = false, isBulk, isDisabled }) => {
  const { cart } = useCart()
  const { bulkOrderEligibilityError, eligibleToBulkOrder } = useBulkMeter()
  const { updateBulkDtcTag } = useOrderTags()
  const [buttonIsLoading, setButtonIsLoading] = useState(false)
  const [apiError, setApiError] = useState(null)
  const translate = useTranslate()
  const checkoutUiText = useUiTextQuery('global', 'checkout')

  const form = useForm()
  const { getValues, handleSubmit, watch } = form
  const isAgeVerified = watch('ageVerification', true)

  const { totalQuantity } = cart

  const onSubmit = async (data) => {
    if (buttonIsLoading || !isAgeVerified) return

    setButtonIsLoading(true)
    setApiError(null)

    try {
      if (isBulk && !eligibleToBulkOrder) {
        setApiError(bulkOrderEligibilityError)
        setButtonIsLoading(false)
        return
      }

      await updateBulkDtcTag()
    } catch (error) {}

    try {
      if (includeSmsOptIn) {
        storeTransactionalNumber(getValues('phoneNumber'))
      }

      await identify('', { verified_age: true })
    } catch (error) {
      setApiError(translate('error.api.default'))
      console.error(error, { source: 'CheckoutButton' })
    }

    setButtonIsLoading(false)
    navigate('/checkout/')
  }

  useEffect(() => {
    setApiError(null)
  }, [totalQuantity])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {includeSmsOptIn && (
        <Box mb={2}>
          <AttentiveOptIn includeAgeVerification={true} parentForm={form} />
        </Box>
      )}

      <Button
        disabled={!isAgeVerified || isDisabled}
        mb={2}
        sx={{
          width: '100%',
        }}
      >
        {buttonIsLoading && (
          <Spinner
            title="Loading checkout"
            size="15"
            color="inherit"
            sx={{
              '@media (prefers-reduced-motion)': {
                circle: {
                  animationIterationCount: 8,
                },
              },
            }}
          />
        )}

        {!buttonIsLoading && isAgeVerified && (
          <Text>{translate('checkout.button')}</Text>
        )}
        {!buttonIsLoading && !isAgeVerified && (
          <Text>{translate('checkout.age_validation')}</Text>
        )}
      </Button>

      {apiError && (
        <Text as="p" variant="error" mb={2}>
          {apiError}
        </Text>
      )}

      {!includeSmsOptIn && (
        <Box mb={1}>
          <AgeVerification parentForm={form} />
        </Box>
      )}

      <Text as="p" color="greyDark" variant="finePrint">
        {checkoutUiText.fine_print}
      </Text>
    </form>
  )
}

export default CheckoutButton
